import type { ConnectionState, HealthStatus } from '@/types';
import { store } from '@/state';

/**
 * Update the WebSocket connection status display
 * @param text - Status text to display
 * @param state - Connection state
 */
export function updateConnectionStatus(text: string, state: ConnectionState): void {
  const statusText = document.getElementById('ws-status-text');
  const status = document.getElementById('ws-status');

  if (statusText) {
    statusText.textContent = text;
  }
  if (status) {
    status.className = `ws-status ${state}`;
  }
}

/**
 * Update all statistics displays
 */
export function updateStatsDisplay(): void {
  const state = store.getState();
  const { stats, embers } = state;

  // Update main stats
  updateElement('total-flows', stats.totalFlows.toLocaleString());
  updateElement('ember-count', embers.length.toLocaleString());
  updateElement('total-bandwidth', stats.currentBandwidth.toFixed(1));

  // Update HUD stats
  updateElement('hud-fps', stats.fps.toString());
  updateElement('hud-flows-sec', stats.flowRate.toString());
  updateElement('hud-latency', Math.round(12 + Math.random() * 5).toString());

  // Update health indicators
  updateHealthIndicators();
}

/**
 * Update a single element's text content
 * @param id - Element ID
 * @param value - New text value
 */
function updateElement(id: string, value: string): void {
  const element = document.getElementById(id);
  if (element) {
    element.textContent = value;
  }
}

/**
 * Update health indicator styles based on thresholds
 */
function updateHealthIndicators(): void {
  const state = store.getState();
  const { stats } = state;

  // Flows health (based on rate)
  const flowsHealth = document.getElementById('flows-health');
  if (flowsHealth) {
    flowsHealth.className = `health-status-indicator ${getFlowsHealthStatus(stats.flowRate)}`;
  }

  // Bandwidth health
  const bandwidthHealth = document.getElementById('bandwidth-health');
  if (bandwidthHealth) {
    bandwidthHealth.className = `health-status-indicator ${getBandwidthHealthStatus(stats.currentBandwidth)}`;
  }

  // Embers health (based on count)
  const embersHealth = document.getElementById('embers-health');
  if (embersHealth) {
    embersHealth.className = `health-status-indicator ${getEmbersHealthStatus(state.embers.length)}`;
  }
}

/**
 * Get health status for flow rate
 */
function getFlowsHealthStatus(flowRate: number): HealthStatus {
  if (flowRate > 200) return 'critical';
  if (flowRate > 100) return 'warning';
  return 'ok';
}

/**
 * Get health status for bandwidth
 */
function getBandwidthHealthStatus(bandwidth: number): HealthStatus {
  if (bandwidth > 500) return 'critical';
  if (bandwidth > 200) return 'warning';
  return 'ok';
}

/**
 * Get health status for ember count
 */
function getEmbersHealthStatus(count: number): HealthStatus {
  if (count > 4000) return 'critical';
  if (count > 2500) return 'warning';
  return 'ok';
}

/**
 * Show or hide the pause indicator
 * @param visible - Whether to show the indicator
 */
export function setPauseIndicatorVisible(visible: boolean): void {
  const indicator = document.getElementById('pause-indicator');
  if (indicator) {
    if (visible) {
      indicator.classList.add('visible');
    } else {
      indicator.classList.remove('visible');
    }
  }
}
